import { useEffect, useState, useCallback } from 'react';
import { HolidayWarning } from './useHolidays';
import { Trash, ClipboardText, User, Heartbeat, UserPlus, Phone } from '@phosphor-icons/react';
import VolunteerComboSearch from './VolunteerComboSearch';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useStatuses } from '../../hooks/useStatuses';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { MiniDayView } from './MiniDayView';
import { fmtDate } from './agendaConstants';
import VolunteerModal from '../../pages/VolunteerModal';
import ConfirmDeleteDialog from '../ConfirmDeleteDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Self-contained appointment form dialog. Embeds the same UI as the Agenda
 * page (left form + right MiniDayView preview) so that it can be reused from
 * any page (Agenda, VolunteersPanel, ...) without taking the user away from
 * their current context.
 *
 * Props:
 *  - open, onClose
 *  - editing: existing appointment object (null = new)
 *  - preselect: { studyId, volunteerId, volunteerName, visitCode } for new
 *  - lang
 *  - isAdmin
 *  - onSaved(): callback after successful create/update/delete
 */
export default function AppointmentFormDialog({
  open, onClose,
  editing = null,
  preselect = null,
  lang = 'es',
  isAdmin = false,
  onSaved,
}) {
  // URS-F416b: helper defensivo para asegurar que toast.error() siempre
  // recibe una string. FastAPI puede devolver detail como objeto
  // ({code,message,appointment}) — pasarlo tal cual a sonner hace crash
  // React con "Objects are not valid as a React child".
  const errText = (err, fallback = 'Error') => {
    const d = err?.response?.data?.detail;
    if (!d) return fallback;
    if (typeof d === 'string') return d;
    if (typeof d === 'object') return d.message || d.code || fallback;
    return String(d);
  };

  const routerNavigate = useNavigate();
  // URS-F348: botón "Revisar Qsalud" gateado por permiso transversal `qsalud`.
  const { can } = usePermissions();
  const canSeeQsalud = can('qsalud', 'view');
  // URS-F486: estados de cita configurables por admin
  const { statuses: apptStatuses, byKey: apptStatusByKey, defaultKey: apptDefaultStatus } = useStatuses('appointment');

  // --- form state ---
  const [form, setForm] = useState({ study_id: '', volunteer_id: '', volunteer_name: '', visit_code: '', date: fmtDate(new Date()), time: '08:00', status: 'citada', notes: '' });
  const [isQsalud, setIsQsalud] = useState(false);
  const [saving, setSaving] = useState(false);

  // --- catalogs ---
  const [studies, setStudies] = useState([]);
  const [studyVols, setStudyVols] = useState([]);
  const [studyVisits, setStudyVisits] = useState([]);
  const [allVolunteers, setAllVolunteers] = useState([]);
  const [scheduleSlots, setScheduleSlots] = useState([]);
  const [scheduleBlocks, setScheduleBlocks] = useState([]);
  const [appointments, setAppointments] = useState([]);

  // --- secondary dialogs ---
  const [showOutOfSchedule, setShowOutOfSchedule] = useState(false);
  // URS-F341: recordamos si el usuario pulso "Guardar e imprimir" para que
  // el flujo "Crear igualmente" tras OutOfSchedule conserve la intencion
  // de imprimir el ticket.
  const [pendingPrint, setPendingPrint] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingAppt, setDeletingAppt] = useState(false);
  const [viewVolunteerId, setViewVolunteerId] = useState(null);
  // URS-F249: ficha completa del voluntario en overlay con iframe.
  const [showVolunteerFull, setShowVolunteerFull] = useState(false);
  // Existing appointment with the same visit_code on the same study (409 handler).
  const [duplicateConflict, setDuplicateConflict] = useState(null); // {appointment_id,date,time,visit_code,status}
  const [cancellingDup, setCancellingDup] = useState(false);
  // Qsalud review gate (only relevant for VAL visits)
  const [qsaludReviewedAt, setQsaludReviewedAt] = useState(null);
  const [showQsaludReview, setShowQsaludReview] = useState(false);
  // URS-F416: aviso de cobertura por bloqueos personales de IP/CoIP.
  // { coverage, staff, blocked, available } — modal con confirmacion.
  const [coverageWarning, setCoverageWarning] = useState(null);
  const [coverageAck, setCoverageAck] = useState(false);
  const [checkingCoverage, setCheckingCoverage] = useState(false);

  const lb = lang === 'es'
    ? { new: 'Nueva Cita', edit: 'Editar Cita', study: 'Estudio', volunteer: 'Voluntario', visit: 'Visita', date: 'Fecha', time: 'Hora', status: 'Estado', notes: 'Notas', save: 'Guardar', cancel: 'Cancelar', delete: 'Eliminar', citada: 'Citada', anulada: 'Anulada', no_asiste: 'No asiste', sala_espera: 'Sala de espera', especialista: 'Especialista', sondas: 'Sondas', terminada: 'Terminada', created: 'Cita creada', updated: 'Cita actualizada', deleted: 'Cita eliminada', select_study: 'Seleccionar estudio', select_volunteer: 'Seleccionar voluntario', select_visit: 'Seleccionar visita' }
    : { new: 'New Appointment', edit: 'Edit Appointment', study: 'Study', volunteer: 'Volunteer', visit: 'Visit', date: 'Date', time: 'Time', status: 'Status', notes: 'Notes', save: 'Save', cancel: 'Cancel', delete: 'Delete', citada: 'Scheduled', anulada: 'Cancelled', no_asiste: 'No show', sala_espera: 'Waiting room', especialista: 'Specialist', sondas: 'Probes', terminada: 'Completed', created: 'Appointment created', updated: 'Appointment updated', deleted: 'Appointment deleted', select_study: 'Select study', select_volunteer: 'Select volunteer', select_visit: 'Select visit' };

  // URS-F254: si la cita se crea para HOY, sugerir hora actual redondeada a
  // multiplos de 5 minutos en lugar del 08:00 fijo.
  const suggestedTimeForDate = (dateStr) => {
    const today = fmtDate(new Date());
    if (dateStr !== today) return '08:00';
    const now = new Date();
    let mins = now.getHours() * 60 + now.getMinutes();
    mins = Math.ceil(mins / 5) * 5;
    if (mins < 8 * 60) mins = 8 * 60;
    if (mins > 20 * 60 + 55) mins = 20 * 60 + 55;
    return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
  };

  // ----------------------------------------------------------------
  // Load static catalogs once when opened
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!open) return;
    Promise.all([
      axios.get(`${API_URL}/api/studies`, { withCredentials: true }),
      axios.get(`${API_URL}/api/schedule/all`, { withCredentials: true }),
      axios.get(`${API_URL}/api/schedule/blocks`, { withCredentials: true }),
    ]).then(([sr, scr, bks]) => {
      setStudies(sr.data || []);
      setScheduleSlots(scr.data || []);
      setScheduleBlocks(Array.isArray(bks.data) ? bks.data : []);
    }).catch(() => {});
  }, [open]);

  // ----------------------------------------------------------------
  // Initialise form when dialog opens (new vs edit vs preselect)
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!open) return;
    if (editing) {
      const qsalud = !editing.study_id || editing.visit_code === 'Qsalud';
      setIsQsalud(qsalud);
      setForm({
        study_id: editing.study_id || '',
        volunteer_id: editing.volunteer_id,
        volunteer_name: editing.volunteer_name || '',
        visit_code: editing.visit_code || '',
        date: editing.date,
        time: editing.time || '',
        status: editing.status,
        notes: editing.notes || '',
      });
      if (qsalud) { setStudyVols([]); setStudyVisits([]); }
      else { loadStudyData(editing.study_id); }
    } else if (preselect && preselect.studyId) {
      // From volunteers page or similar
      setIsQsalud(false);
      const d = fmtDate(new Date());
      setForm({
        study_id: preselect.studyId,
        volunteer_id: preselect.volunteerId || '',
        volunteer_name: preselect.volunteerName || '',
        visit_code: preselect.visitCode || '',
        date: d,
        time: suggestedTimeForDate(d),
        status: apptDefaultStatus || 'citada',
        notes: '',
      });
      loadStudyData(preselect.studyId);
    } else {
      // Brand new from agenda "+"
      setIsQsalud(false);
      const d = fmtDate(new Date());
      setForm({ study_id: '', volunteer_id: '', volunteer_name: '', visit_code: '', date: d, time: suggestedTimeForDate(d), status: apptDefaultStatus || 'citada', notes: '' });
      setStudyVols([]); setStudyVisits([]); setAllVolunteers([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, preselect?.studyId, preselect?.volunteerId, preselect?.visitCode, apptDefaultStatus]);

  // ----------------------------------------------------------------
  // Reload appointments for the day shown in the preview (right pane)
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!open || !form.date) return;
    const params = new URLSearchParams({ date_from: form.date, date_to: form.date });
    axios.get(`${API_URL}/api/appointments?${params}`, { withCredentials: true })
      .then(r => setAppointments(r.data || []))
      .catch(() => setAppointments([]));
  }, [open, form.date]);

  // ----------------------------------------------------------------
  // Helpers
  // ----------------------------------------------------------------
  const loadStudyData = async (studyId) => {
    if (!studyId) { setStudyVols([]); setStudyVisits([]); return; }
    try {
      const [vr, visr] = await Promise.all([
        axios.get(`${API_URL}/api/studies/${studyId}/volunteers`, { withCredentials: true }),
        axios.get(`${API_URL}/api/studies/${studyId}/visits`, { withCredentials: true }),
      ]);
      setStudyVols(vr.data || []);
      setStudyVisits(visr.data || []);
    } catch { setStudyVols([]); setStudyVisits([]); }
  };

  const handleStudyChange = (studyId) => {
    if (studyId === '_qsalud' || studyId === '_qsalud_tfno') {
      setIsQsalud(true);
      setForm(p => ({ ...p, study_id: '', volunteer_id: '', visit_code: studyId === '_qsalud_tfno' ? 'QSALUD TFNO' : 'Qsalud' }));
      setStudyVols([]); setStudyVisits([]);
      axios.get(`${API_URL}/api/volunteers?all=true`, { withCredentials: true })
        .then(r => {
          const list = Array.isArray(r.data) ? r.data : (r.data?.data || []);
          setAllVolunteers(list);
        })
        .catch(() => setAllVolunteers([]));
    } else {
      setIsQsalud(false);
      setForm(p => ({ ...p, study_id: studyId, volunteer_id: '', visit_code: '' }));
      setAllVolunteers([]);
      loadStudyData(studyId);
    }
  };

  const isInSchedule = useCallback((studyId, date, time) => {
    if (!studyId || !date || !time) return true;
    const studySlots = scheduleSlots.filter(s => s.study_id === studyId);
    if (studySlots.length === 0) return true;
    return studySlots.some(s => s.date === date && s.start_time <= time && s.end_time > time);
  }, [scheduleSlots]);

  // Check if current form selection overlaps a block
  const activeBlock = (() => {
    if (!form.study_id || !form.date || !form.time || isQsalud) return null;
    return scheduleBlocks.find(b => b.study_id === form.study_id && b.date === form.date && b.start_time <= form.time && b.end_time > form.time) || null;
  })();

  // ----------------------------------------------------------------
  // Qsalud review gate (only for Valoracion visits)
  // A visit counts as "Valoracion" / "Screening" if either:
  //   - its code matches VAL / VALORACION / SCREENING / SCR  (case-insensitive)
  //   - or its display name (accent-insensitive) starts with valoracion / screening
  //   - or it is the FIRST visit of the study (day === 0 or order === 0)
  //     and the user explicitly typed something matching valor/screen
  // ----------------------------------------------------------------
  const stripAccents = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const isValVisit = (() => {
    const code = (form.visit_code || '').toUpperCase().trim();
    if (!code) return false;
    if (/^(VAL|VALORACION|VALORACI[OÓ]N|SCREENING|SCR|S0|V0)$/i.test(code)) return true;
    // Look up by code in current studyVisits to also inspect the type/name.
    // URS-F463 fix: NO tratar "basal" como Valoracion. La basal es la primera
    // visita del protocolo y saca voluntarios de "Aptos", no de "Pendientes".
    const v = studyVisits.find(x => (x.code || '').toUpperCase() === code);
    if (v) {
      if (v.visit_type === 'valoracion') return true;
      const n = stripAccents(v.name);
      if (n.startsWith('valoracion') || n.startsWith('screening') || n === 'cribado') return true;
    }
    return false;
  })();

  const qsaludRecent = (() => {
    if (!qsaludReviewedAt) return false;
    const ts = new Date(qsaludReviewedAt).getTime();
    if (Number.isNaN(ts)) return false;
    return (Date.now() - ts) < 24 * 60 * 60 * 1000;
  })();
  const qsaludGateOpen = !isValVisit || qsaludRecent;

  // Fetch qsalud_last_reviewed_at when volunteer changes (or dialog opens)
  useEffect(() => {
    if (!form.volunteer_id || !open) {
      setQsaludReviewedAt(null);
      return;
    }
    let cancelled = false;
    axios.get(`${API_URL}/api/volunteers/${form.volunteer_id}`, { withCredentials: true })
      .then(r => { if (!cancelled) setQsaludReviewedAt(r.data?.qsalud_last_reviewed_at || null); })
      .catch(() => { if (!cancelled) setQsaludReviewedAt(null); });
    return () => { cancelled = true; };
  }, [form.volunteer_id, open]);

  // Listen for postMessage from the embedded VolunteerForm (Qsalud popup)
  useEffect(() => {
    const handler = (ev) => {
      if (!ev?.data || ev.data.type !== 'qsalud_saved') return;
      if (form.volunteer_id && ev.data.volunteer_id === form.volunteer_id) {
        setQsaludReviewedAt(ev.data.at || new Date().toISOString());
        toast.success(lang === 'es' ? 'Qsalud guardado y revisado' : 'Qsalud saved and reviewed');
        setShowQsaludReview(false);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [form.volunteer_id, lang]);

  const goToFillQuestionnaire = () => {
    onClose();
    if (isValVisit) {
      // For Valoracion the I/E questionnaire is filled from the "Pendientes" tab
      routerNavigate(`/studies/${form.study_id}?tab=volunteers&volunteers_tab=pendientes&highlight_volunteer=${form.volunteer_id}`);
    } else {
      routerNavigate(`/studies/${form.study_id}?tab=volunteers&volunteer_id=${form.volunteer_id}`);
    }
  };

  // ----------------------------------------------------------------
  // Volunteer list filtered by selected visit (Estudio → Visita → Voluntario)
  // ----------------------------------------------------------------
  const isT0Visit = (() => {
    const code = (form.visit_code || '').toUpperCase().trim();
    if (!code) return false;
    if (/^T0$/i.test(code) || /^TIEMPO\s*0$/i.test(code) || /^BASELINE$/i.test(code)) return true;
    const v = studyVisits.find(x => (x.code || '').toUpperCase() === code);
    if (v) {
      const n = stripAccents(v.name);
      if (n === 'tiempo 0' || n.startsWith('basal') || n.startsWith('baseline')) return true;
    }
    // URS-F463: la "basal" es dinamicamente la primera visita del protocolo
    // (menor `day`, excluyendo VAL y Eventos Adversos). Esa visita saca
    // voluntarios de "Aptos" aunque su codigo no sea T0 (p.ej. T-14D).
    const protocol = (studyVisits || []).filter(x => {
      const c = (x.code || '').toUpperCase();
      return c && c !== 'VAL' && c !== 'EA' && x.visit_type !== 'valoracion' && x.visit_type !== 'eventos_adversos';
    });
    if (protocol.length === 0) return false;
    const sorted = [...protocol].sort((a, b) => (a.day ?? 0) - (b.day ?? 0));
    const firstCode = (sorted[0]?.code || '').toUpperCase();
    return !!firstCode && firstCode === code;
  })();

  const volunteersForAppointment = (() => {
    if (isQsalud) {
      return (allVolunteers || []).map(v => {
        const pd = v.personal_data || {};
        const name = `${pd.nombre || v.name || ''} ${pd.apellidos || v.surname || ''}`.trim() || v.volunteer_id;
        return { ...v, volunteer_name: name, volunteer_dni: pd.dni || '', study_volunteer_code: pd.num_voluntario || '' };
      });
    }
    if (!form.study_id || !form.visit_code) return [];
    let allowed;
    if (isValVisit) allowed = ['pendiente'];
    else if (isT0Visit) allowed = ['apto'];
    else allowed = ['incluido'];
    let pool = (studyVols || []).filter(sv => allowed.includes(sv.status));
    if (editing && form.volunteer_id && !pool.some(p => p.volunteer_id === form.volunteer_id)) {
      const cur = (studyVols || []).find(sv => sv.volunteer_id === form.volunteer_id);
      if (cur) pool = [cur, ...pool];
    }
    return pool;
  })();

  const volunteerEmptyHint = (() => {
    if (isQsalud || !form.study_id || !form.visit_code) return lang === 'es' ? 'Sin coincidencias.' : 'No matches.';
    if (isValVisit) return lang === 'es' ? 'No hay voluntarios pendientes en este estudio.' : 'No pending volunteers in this study.';
    if (isT0Visit) return lang === 'es' ? 'No hay voluntarios aptos en este estudio.' : 'No fit volunteers in this study.';
    return lang === 'es' ? 'No hay voluntarios incluidos en este estudio.' : 'No included volunteers in this study.';
  })();

  const volunteerListEmpty = !isQsalud && form.study_id && form.visit_code && volunteersForAppointment.length === 0;

  const formatVolForCombo = (o) => {
    if (isQsalud) return `${o.study_volunteer_code ? `${o.study_volunteer_code} — ` : ''}${o.volunteer_name || o.volunteer_id}`;
    return `${o.study_volunteer_code || ''} — ${o.volunteer_name || o.volunteer_id}`.replace(/^—\s*/, '');
  };
  // ----------------------------------------------------------------
  // Save / Delete
  // ----------------------------------------------------------------
  const handleSave = async () => {
    if (isQsalud) {
      if (!form.volunteer_id || !form.date) {
        toast.error(lang === 'es' ? 'Voluntario y fecha son obligatorios' : 'Volunteer and date are required');
        return;
      }
    } else {
      if (!form.study_id || !form.volunteer_id || !form.date) {
        toast.error(lang === 'es' ? 'Estudio, voluntario y fecha son obligatorios' : 'Study, volunteer and date are required');
        return;
      }
      if (activeBlock) {
        toast.error(lang === 'es'
          ? `Hueco bloqueado: ${activeBlock.reason || ''} (${activeBlock.start_time}-${activeBlock.end_time})`
          : `Slot blocked: ${activeBlock.reason || ''} (${activeBlock.start_time}-${activeBlock.end_time})`);
        return;
      }
      if (!isInSchedule(form.study_id, form.date, form.time)) {
        setShowOutOfSchedule(true);
        return;
      }
    }
    await doSave();
  };

  // URS-F341 v3: recibe la ventana ya abierta (sincronizada con el click).
  // Solo escribe el HTML y dispara window.print().
  const printAppointmentTicket = (printWin) => {
    const w = printWin || window.open('', '_blank');
    if (!w) {
      toast.error(lang === 'es' ? 'Habilita las ventanas emergentes para imprimir.' : 'Allow popups to print.');
      return;
    }
    const study = studies.find(s => s.study_id === form.study_id);
    // URS-F491 (privacidad/ciego): NO imprimir el NOMBRE del voluntario ni
    // el NOMBRE/CODIGO del estudio. Sustituimos el nombre del voluntario
    // por el `study_volunteer_code` (fallback al volunteer_id). El
    // SUBTIPO del estudio (phase) si se imprime porque es informacion
    // tecnica no identificativa. Si el estudio no tiene subtipo
    // (ej. Medicamento) se imprime en blanco: NUNCA hacemos fallback al
    // codigo del estudio (rompia el ciego).
    const studyPhase = study?.phase || '';
    const selectedVol = volunteersForAppointment.find(v => v.volunteer_id === form.volunteer_id);
    const volCode = selectedVol?.study_volunteer_code || form.volunteer_id || '—';
    const row = `<tr>
        <td class="c-date">${form.date || '-'}</td>
        <td class="c-time">${form.time || '-'}</td>
        <td class="c-visit">${form.visit_code || ''}<span class="study-sub">${studyPhase || ''}</span></td>
      </tr>`;
    const logoUrl = window.location.origin + '/logo-goya.png';
    const html = `<!DOCTYPE html><html><head><title>Cita ${form.visit_code || ''}</title><style>
      @page { size: 80mm auto; margin: 0 1mm 1mm 1mm; }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: 'Helvetica Neue', Arial, sans-serif; width: 76mm; padding: 0 1mm 1.5mm 1mm; color: #000; font-size: 12px; line-height: 1.3; }
      .ticket { page-break-after: auto; }
      .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 1.5mm; margin-bottom: 1.5mm; }
      .header img { width: 55mm; height: auto; margin-bottom: 0.5mm; filter: grayscale(100%) contrast(160%); }
      .header .phone { font-size: 11px; margin-top: 0.8mm; font-weight: 600; }
      .header .ev-clinicas { font-size: 10px; margin-top: 0.6mm; letter-spacing: 0.3px; }
      .header .name { font-size: 12px; font-weight: 700; margin-top: 1.5mm; text-transform: uppercase; line-height: 1.15; }
      .header .subtitle { font-size: 11px; margin-top: 1mm; font-weight: 600; letter-spacing: 1px; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 1.5mm; }
      th { font-size: 10px; text-align: left; border-bottom: 2px solid #000; padding: 1mm 1mm; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; }
      td { font-size: 13px; padding: 2mm 1mm; border-bottom: 1px dashed #999; font-weight: 600; vertical-align: top; }
      td.c-date { white-space: nowrap; }
      td.c-time { font-weight: 700; }
      td.c-visit { font-weight: 700; }
      td.c-visit .study-sub { display: block; font-size: 9px; font-weight: 500; color: #000; margin-top: 0.4mm; text-transform: uppercase; letter-spacing: 0.3px; }
    </style></head><body><div class="ticket">
      <div class="header">
        <img src="${logoUrl}" alt="Goya Innova" />
        <div class="phone">Tel: 911 928 904</div>
        <div class="ev-clinicas">Evaluaciones Cl\u00ednicas</div>
        <div class="name">${volCode}</div>
        <div class="subtitle">CITA</div>
      </div>
      <table><thead><tr><th>Fecha</th><th>Hora</th><th>Visita</th></tr></thead><tbody>${row}</tbody></table>
      </div>
      <script>window.onload=function(){window.print();}<\/script>
    </body></html>`;
    w.document.open();
    w.document.writeln(html);
    w.document.close();
  };

  const handleSaveAndPrint = async () => {
    if (isQsalud) {
      if (!form.volunteer_id || !form.date) {
        toast.error(lang === 'es' ? 'Voluntario y fecha son obligatorios' : 'Volunteer and date are required');
        return;
      }
    } else {
      if (!form.study_id || !form.volunteer_id || !form.date) {
        toast.error(lang === 'es' ? 'Estudio, voluntario y fecha son obligatorios' : 'Study, volunteer and date are required');
        return;
      }
      if (activeBlock) {
        toast.error(lang === 'es'
          ? `Hueco bloqueado: ${activeBlock.reason || ''} (${activeBlock.start_time}-${activeBlock.end_time})`
          : `Slot blocked: ${activeBlock.reason || ''} (${activeBlock.start_time}-${activeBlock.end_time})`);
        return;
      }
      if (!isInSchedule(form.study_id, form.date, form.time)) {
        setPendingPrint(true);
        setShowOutOfSchedule(true);
        return;
      }
    }
    await doSave({ printAfter: true });
  };

  // URS-F416: helper para calcular el fin del slot (fallback: +30 min).
  const _endOf = (start) => {
    if (!start || !/^\d{2}:\d{2}$/.test(start)) return start || '08:30';
    const [h, m] = start.split(':').map(Number);
    let total = h * 60 + m + 30;
    if (total >= 24 * 60) total = 24 * 60 - 1;
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  };

  // URS-F416: consulta la cobertura del staff para el slot solicitado.
  // Devuelve el payload del backend o null si algo falla / no aplica.
  const checkCoverage = async (opts = {}) => {
    if (isQsalud) return null;
    if (!form.study_id || !form.date || !form.time) return null;
    if (opts.skip) return null;
    setCheckingCoverage(true);
    try {
      const resp = await axios.post(
        `${API_URL}/api/user-schedule-blocks/coverage`,
        {
          study_id: form.study_id,
          date: form.date,
          start_time: form.time,
          end_time: _endOf(form.time),
        },
        { withCredentials: true },
      );
      return resp.data || null;
    } catch (err) {
      // Si el endpoint falla no bloqueamos el flujo — solo perdemos el aviso.
      return null;
    } finally {
      setCheckingCoverage(false);
    }
  };

  const doSave = async (opts = {}) => {
    // URS-F416: gate de cobertura solo para citas NUEVAS (edicion no cambia
    // asignacion) y saltable con opts.skipCoverage (post-confirmacion del
    // usuario en el warning).
    const movedEdit = editing && (editing.date !== form.date || editing.time !== form.time);
    if ((!editing || movedEdit) && !opts.skipCoverage) {
      const cov = await checkCoverage();
      if (cov && (cov.coverage === 'partial' || cov.coverage === 'none') && (cov.staff?.length || 0) > 0) {
        setCoverageWarning(cov);
        setCoverageAck(false);
        return;
      }
    }
    setShowOutOfSchedule(false);
    setSaving(true);
    // URS-F341 v3: si el caller ya abrio la ventana (opts.printWin), la
    // reutilizamos. Si no y se pide imprimir, abrimos aqui (sincrono al click).
    const printWin = opts.printAfter ? (opts.printWin || window.open('', '_blank')) : null;
    if (opts.printAfter && !printWin) {
      toast.error(lang === 'es' ? 'Habilita las ventanas emergentes para imprimir.' : 'Allow popups to print.');
      setSaving(false);
      return;
    }
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/appointments/${editing.appointment_id}`, { date: form.date, time: form.time, status: form.status, notes: form.notes, visit_code: form.visit_code }, { withCredentials: true });
        toast.success(lb.updated);
      } else {
        const payload = isQsalud
          ? { study_id: 'none', volunteer_id: form.volunteer_id, volunteer_name: form.volunteer_name, visit_code: form.visit_code === 'QSALUD TFNO' ? 'QSALUD TFNO' : 'Qsalud', date: form.date, time: form.time, status: form.status, notes: form.notes }
          : form;
        await axios.post(`${API_URL}/api/appointments`, payload, { withCredentials: true });
        toast.success(lb.created);
      }
      if (printWin) {
        printAppointmentTicket(printWin);
      }
      onClose();
      if (onSaved) onSaved();
    } catch (err) {
      // Si el guardado fallo, cerramos la ventana de impresion que abrimos a-priori.
      if (printWin && !printWin.closed) { try { printWin.close(); } catch { /* noop */ } }
      // Backend signals an existing-active-visit conflict with HTTP 409 and a
      // structured detail. Surface a dedicated dialog so the user can either
      // edit the existing appointment or cancel it.
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'duplicate_visit_appointment') {
        setDuplicateConflict(detail.appointment || null);
      } else {
        toast.error(errText(err));
      }
    }
    setSaving(false);
  };

  // Cancel (anular) the conflicting appointment so the user can immediately
  // create a fresh one with the new date/time.
  const handleCancelDuplicate = async () => {
    if (!duplicateConflict?.appointment_id) return;
    setCancellingDup(true);
    try {
      await axios.put(`${API_URL}/api/appointments/${duplicateConflict.appointment_id}`,
        { status: 'anulada', notes: lang === 'es' ? 'Anulada para reprogramar visita' : 'Cancelled to reschedule visit' },
        { withCredentials: true });
      toast.success(lang === 'es' ? 'Cita anterior anulada' : 'Previous appointment cancelled');
      setDuplicateConflict(null);
      await doSave();
    } catch (err) {
      toast.error(errText(err));
    }
    setCancellingDup(false);
  };

  // Switch the current form to edit the conflicting appointment instead.
  const handleEditDuplicate = async () => {
    if (!duplicateConflict?.appointment_id) return;
    try {
      const { data: appt } = await axios.get(`${API_URL}/api/appointments/${duplicateConflict.appointment_id}`, { withCredentials: true });
      setForm({
        study_id: appt.study_id || form.study_id,
        volunteer_id: appt.volunteer_id,
        volunteer_name: appt.volunteer_name || form.volunteer_name,
        visit_code: appt.visit_code,
        date: appt.date,
        time: appt.time || '',
        status: appt.status,
        notes: appt.notes || '',
      });
      // Force the dialog into edit mode for that appointment by closing &
      // reopening through onSaved (which the caller can use to re-open as edit)
      setDuplicateConflict(null);
      toast.info(lang === 'es' ? 'Editando la cita existente' : 'Editing existing appointment');
      // Notify the parent so it can switch into edit mode with this appointment.
      if (onSaved) onSaved(appt);
      onClose();
    } catch {
      toast.error('Error');
    }
  };

  const handleDelete = async () => {
    if (!editing) return;
    setDeletingAppt(true);
    try {
      await axios.delete(`${API_URL}/api/appointments/${editing.appointment_id}`, { withCredentials: true });
      toast.success(lb.deleted);
      setShowDeleteConfirm(false);
      onClose();
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(errText(err));
    }
    setDeletingAppt(false);
  };

  const activeStudies = studies.filter(s => s.status === 'activo');
  const activeStudiesWithVisits = activeStudies.filter(s => (s.visit_count || 0) > 0);

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) onClose(); }}>
        <DialogContent className="max-w-6xl p-0 overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:h-[620px]">
            {/* LEFT: Form */}
            <div className="flex-1 flex flex-col overflow-y-auto min-w-0">
              <DialogHeader className="px-6 pt-6 pb-2">
                <DialogTitle>{editing ? lb.edit : lb.new}</DialogTitle>
              </DialogHeader>
              <div className="flex-1 px-6 pb-2 space-y-4 overflow-y-auto">
                <div className="space-y-1.5">
                  <Label>{lb.study}</Label>
                  <Select value={isQsalud ? (form.visit_code === 'QSALUD TFNO' ? '_qsalud_tfno' : '_qsalud') : (form.study_id || '_none')} onValueChange={v => handleStudyChange(v === '_none' ? '' : v)} disabled={!!editing}>
                    <SelectTrigger data-testid="appt-study-select"><SelectValue placeholder={lb.select_study} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">{lb.select_study}</SelectItem>
                      <SelectItem value="_qsalud">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-emerald-500" />
                          Qsalud — {lang === 'es' ? 'Sin estudio' : 'No study'}
                        </span>
                      </SelectItem>
                      <SelectItem value="_qsalud_tfno" data-testid="appt-study-qsalud-tfno">
                        <span className="flex items-center gap-2">
                          <Phone size={12} className="text-emerald-600" />
                          QSALUD TFNO — {lang === 'es' ? 'Sin estudio (telefónica)' : 'No study (phone)'}
                        </span>
                      </SelectItem>
                      {activeStudiesWithVisits.map(s => (
                        <SelectItem key={s.study_id} value={s.study_id}>
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color || '#6B7280' }} />
                            {s.code} — {s.title}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {isQsalud && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-sm">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    <span className="text-xs font-medium text-emerald-700">{form.visit_code === 'QSALUD TFNO' ? (lang === 'es' ? 'Cita QSALUD TFNO — Revisión telefónica del Qsalud, sin estudio asociado (sin recordatorios)' : 'QSALUD TFNO appointment — Phone Qsalud review, no study (no reminders)') : (lang === 'es' ? 'Cita Qsalud — Visita general sin estudio asociado' : 'Qsalud appointment — General visit without study')}</span>
                  </div>
                )}
                {/* 2) Visit (skipped for Qsalud) */}
                {isQsalud ? (
                  <div className="space-y-1.5">
                    <Label>{lb.visit}</Label>
                    <Input data-testid="appt-visit-display" value={form.visit_code === 'QSALUD TFNO' ? 'QSALUD TFNO' : 'Qsalud'} readOnly className="bg-stone-50 text-stone-600 cursor-not-allowed" />
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <Label>{lb.visit} <span className="text-red-500">*</span></Label>
                    <Select value={form.visit_code || '_none'} onValueChange={v => setForm(p => ({ ...p, visit_code: v === '_none' ? '' : v, volunteer_id: '', volunteer_name: '' }))}>
                      <SelectTrigger data-testid="appt-visit-select"><SelectValue placeholder={lb.select_visit} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">{lb.select_visit}</SelectItem>
                        {studyVisits.map(v => (
                          <SelectItem key={v.visit_id} value={v.code}>{v.code} — {v.name || `${lang === 'es' ? 'Dia' : 'Day'} ${v.day}`}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {/* 3) Volunteer with inline search, depends on study + visit */}
                <div className="space-y-1.5">
                  <Label>{lb.volunteer} <span className="text-red-500">*</span></Label>
                  <VolunteerComboSearch
                    dataTestid="appt-volunteer-combo"
                    disabled={!!editing || (!isQsalud && (!form.study_id || !form.visit_code))}
                    options={volunteersForAppointment}
                    value={form.volunteer_id || ''}
                    onChange={(id, opt) => {
                      if (!id) {
                        setForm(p => ({ ...p, volunteer_id: '', volunteer_name: '' }));
                        return;
                      }
                      const name = opt?.volunteer_name || (() => {
                        const pd = opt?.personal_data || {};
                        return `${pd.nombre || opt?.name || ''} ${pd.apellidos || opt?.surname || ''}`.trim();
                      })();
                      setForm(p => ({ ...p, volunteer_id: id, volunteer_name: name }));
                    }}
                    getLabel={formatVolForCombo}
                    placeholder={!isQsalud && (!form.study_id || !form.visit_code)
                      ? (lang === 'es' ? 'Selecciona estudio y visita primero...' : 'Select study and visit first...')
                      : (lang === 'es' ? 'Buscar voluntario por nombre, V-code o DNI...' : 'Search by name, V-code or DNI...')}
                    emptyHint={volunteerEmptyHint}
                  />
                  {volunteerListEmpty && (
                    <Button
                      variant="outline"
                      size="sm"
                      type="button"
                      data-testid="assign-volunteer-shortcut"
                      onClick={() => { onClose(); routerNavigate(`/studies/${form.study_id}?tab=volunteers`); }}
                      className="w-full mt-1.5 rounded-sm gap-1 text-xs text-amber-700 border-amber-300 hover:bg-amber-50"
                    >
                      <UserPlus size={12} />
                      {lang === 'es' ? 'Asignar voluntario al estudio' : 'Assign volunteer to study'}
                    </Button>
                  )}
                  {form.volunteer_id && form.volunteer_id !== '_none' && (
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="view-volunteer-btn"
                      onClick={() => setShowVolunteerFull(true)}
                      className="w-full mt-1.5 rounded-sm gap-1 text-xs text-[#1f3e80] border-[#1f3e80]/30 hover:border-[#1f3e80]/50"
                    >
                      <User size={12} />{lang === 'es' ? 'Ver voluntario' : 'View volunteer'}
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>{lb.date} <span className="text-red-500">*</span></Label>
                    <Input data-testid="appt-date-input" type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{lb.time}</Label>
                    <Input data-testid="appt-time-input" type="time" step="900" min="08:00" max="21:00" value={form.time} onChange={e => setForm(p => ({ ...p, time: e.target.value }))} />
                  </div>
                </div>
                <HolidayWarning date={form.date} lang={lang} />
                {editing && editing.duration_minutes && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-stone-50 border border-stone-200 rounded-sm">
                    <span className="text-xs text-stone-500">{lang === 'es' ? 'Duracion' : 'Duration'}:</span>
                    <span className="text-sm font-semibold text-stone-800" data-testid="appt-duration-display">{editing.duration_minutes} min</span>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label>{lb.status}</Label>
                  <Select value={form.status} onValueChange={v => setForm(p => ({ ...p, status: v }))}>
                    <SelectTrigger data-testid="appt-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(apptStatuses && apptStatuses.length > 0
                        ? apptStatuses
                        : ['citada', 'anulada', 'no_asiste', 'sala_espera', 'especialista', 'sondas', 'terminada'].map(k => ({ key: k, label_es: lb[k], label_en: lb[k], color: '#5b6473' }))
                      ).map(s => (
                        <SelectItem key={s.key} value={s.key}>
                          <span className="inline-flex items-center gap-2">
                            <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                            {lang === 'es' ? s.label_es : s.label_en}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>{lb.notes}</Label>
                  <Textarea data-testid="appt-notes-input" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className="min-h-[50px]" />
                </div>
                {activeBlock && (
                  <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-300 rounded-sm" data-testid="appt-block-warning">
                    <span className="text-red-600 text-lg leading-none">⛔</span>
                    <div className="text-xs text-red-700 leading-tight">
                      <div className="font-semibold">{lang === 'es' ? 'Hueco bloqueado para este estudio' : 'Slot blocked for this study'}</div>
                      <div className="mt-0.5">{activeBlock.reason} ({activeBlock.start_time}-{activeBlock.end_time})</div>
                      <div className="mt-0.5 text-red-600">{lang === 'es' ? 'No se puede crear la cita. Elimina el bloqueo o elige otro hueco.' : 'Cannot create appointment. Remove the block or pick another slot.'}</div>
                    </div>
                  </div>
                )}
              </div>
              <div className="px-6 py-3 border-t border-stone-200 flex justify-between">
                <div className="flex gap-2">
                  {/* URS-F246: tambien visible en citas de tipo Qsalud para
                      saltar directo al Qsalud del voluntario. */}
                  {editing && form.volunteer_id && canSeeQsalud && (isQsalud || (form.study_id && isValVisit)) && (
                    <Button
                      variant="outline"
                      data-testid="review-qsalud-btn"
                      onClick={() => setShowQsaludReview(true)}
                      className={`rounded-sm gap-1 leading-tight text-center ${(isQsalud || qsaludRecent) ? 'text-emerald-700 border-emerald-300 hover:border-emerald-500 hover:bg-emerald-50' : 'text-amber-700 border-amber-300 hover:border-amber-500 hover:bg-amber-50'}`}
                      title={isQsalud
                        ? (lang === 'es' ? 'Abrir Qsalud del voluntario' : 'Open volunteer Qsalud')
                        : (qsaludRecent
                          ? (lang === 'es' ? `Qsalud revisado hace menos de 24h (${new Date(qsaludReviewedAt).toLocaleString(lang === 'es' ? 'es-ES' : 'en-US')})` : `Qsalud reviewed less than 24h ago`)
                          : (lang === 'es' ? 'Qsalud no revisado en las últimas 24h' : 'Qsalud not reviewed in the last 24h'))}
                    >
                      <Heartbeat size={14} /><span className="flex flex-col text-xs"><span>{isQsalud ? 'Ir a' : 'Revisar'}</span><span>Qsalud</span></span>
                    </Button>
                  )}
                  {editing && form.study_id && form.volunteer_id && !isQsalud && (
                    <Button
                      variant="outline"
                      data-testid="fill-questionnaire-btn"
                      onClick={goToFillQuestionnaire}
                      disabled={!qsaludGateOpen}
                      className="text-[#1f3e80] hover:text-[#2a4f8a] border-[#1f3e80]/30 hover:border-[#1f3e80]/50 rounded-sm gap-1 leading-tight text-center disabled:opacity-50"
                      title={!qsaludGateOpen ? (lang === 'es' ? 'Revisa primero el Qsalud del voluntario' : 'Review the volunteer Qsalud first') : ''}
                    >
                      <ClipboardText size={14} /><span className="flex flex-col text-xs"><span>Rellenar</span><span>Cuestionario</span></span>
                    </Button>
                  )}
                  {editing && isAdmin && (
                    <Button variant="outline" data-testid="delete-appt-btn" onClick={() => setShowDeleteConfirm(true)} className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300 rounded-sm gap-1">
                      <Trash size={14} />{lb.delete}
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={onClose}>{lb.cancel}</Button>
                  <Button data-testid="save-and-print-appt-btn" onClick={handleSaveAndPrint} disabled={saving} variant="outline" className="rounded-sm border-[#1f3e80]/40 text-[#1f3e80] hover:bg-[#1f3e80]/5">
                    {saving ? '...' : (lang === 'es' ? 'Guardar e imprimir' : 'Save and print')}
                  </Button>
                  <Button data-testid="save-appt-btn" onClick={handleSave} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm">
                    {saving ? '...' : lb.save}
                  </Button>
                </div>
              </div>
            </div>
            {/* RIGHT: mini agenda preview - widened (URS-F224) */}
            <div className="hidden sm:flex flex-col w-[420px] shrink-0 border-l border-stone-200 bg-white">
              <MiniDayView
                date={form.date}
                appointments={appointments}
                studies={studies}
                scheduleSlots={scheduleSlots}
                scheduleBlocks={scheduleBlocks}
                activeStudyId={form.study_id || null}
                selectedTime={form.time}
                lang={lang}
                onSelectTime={(d, t) => setForm(p => ({ ...p, date: d, time: t }))}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Qsalud review modal — opens an iframe to /volunteers/{id}?embedded=1 */}
      <Dialog open={showQsaludReview} onOpenChange={setShowQsaludReview}>
        <DialogContent className="max-w-5xl w-[95vw] h-[90vh] p-0 flex flex-col" data-testid="qsalud-review-dialog">
          <DialogHeader className="px-4 pt-3 pb-2 border-b border-stone-200">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Heartbeat size={18} className="text-emerald-600" />
              {lang === 'es' ? 'Revisar Qsalud' : 'Review Qsalud'}
              <span className="text-xs font-normal text-stone-500 ml-2">
                {form.volunteer_name} · {form.volunteer_id}
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden bg-stone-50">
            {form.volunteer_id && (
              <iframe
                key={form.volunteer_id}
                src={`/volunteers/${form.volunteer_id}?embedded=1&tab=health`}
                title="Qsalud"
                className="w-full h-full border-0"
                data-testid="qsalud-iframe"
              />
            )}
          </div>
          <div className="px-4 py-2 border-t border-stone-200 flex justify-between items-center bg-white">
            <p className="text-xs text-stone-500">
              {lang === 'es'
                ? 'Pulsa "Guardar" dentro del Qsalud para que se considere revisado.'
                : 'Click "Save" inside the Qsalud to mark it as reviewed.'}
            </p>
            <Button variant="ghost" onClick={() => setShowQsaludReview(false)}>{lb.cancel}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* URS-F249: ficha completa del voluntario en overlay iframe */}
      <Dialog open={showVolunteerFull} onOpenChange={setShowVolunteerFull}>
        <DialogContent className="max-w-6xl w-[95vw] h-[92vh] p-0 flex flex-col" data-testid="volunteer-full-dialog">
          <DialogHeader className="px-4 pt-3 pb-2 border-b border-stone-200">
            <DialogTitle className="flex items-center gap-2 text-base">
              <User size={18} className="text-[#1f3e80]" />
              {lang === 'es' ? 'Ficha del voluntario' : 'Volunteer record'}
              <span className="text-xs font-normal text-stone-500 ml-2">
                {form.volunteer_name} · {form.volunteer_id}
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden bg-stone-50">
            {form.volunteer_id && (
              <iframe
                key={`vol-full-${form.volunteer_id}`}
                src={`/volunteers/${form.volunteer_id}?embedded=1`}
                title="Volunteer"
                className="w-full h-full border-0"
                data-testid="volunteer-full-iframe"
              />
            )}
          </div>
          <div className="px-4 py-2 border-t border-stone-200 flex justify-between items-center bg-white">
            <p className="text-xs text-stone-500">
              {lang === 'es'
                ? 'Puedes navegar entre pestanas, enviar documentos a la tableta y editar el Qsalud sin perder esta cita.'
                : 'You can navigate tabs, send documents to the tablet, and edit Qsalud without losing this appointment.'}
            </p>
            <Button variant="ghost" onClick={() => setShowVolunteerFull(false)}>{lb.cancel}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* URS-F416: Modal de cobertura — partial (aviso con confirmacion) o none (bloqueo duro) */}
      <Dialog open={!!coverageWarning} onOpenChange={v => { if (!v) { setCoverageWarning(null); setCoverageAck(false); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="coverage-warning-dialog">
          <DialogHeader>
            <DialogTitle className={coverageWarning?.coverage === 'none' ? 'text-red-700' : 'text-amber-700'}>
              {coverageWarning?.coverage === 'none'
                ? (editing ? (lang === 'es' ? 'No se puede mover la cita: sin cobertura' : 'Cannot move appointment: no coverage') : (lang === 'es' ? 'No se puede crear la cita: sin cobertura' : 'Cannot create appointment: no coverage'))
                : (lang === 'es' ? 'Aviso: cobertura parcial del staff' : 'Warning: partial staff coverage')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-stone-700">
              {coverageWarning?.coverage === 'none'
                ? (lang === 'es'
                  ? 'Ningun IP/CoIP del staff esta disponible en la fecha y hora seleccionadas:'
                  : 'No IP/CoIP of the study staff is available at the selected date/time:')
                : (lang === 'es'
                  ? 'En la fecha y hora seleccionadas hay miembros del staff del estudio con bloqueo personal:'
                  : 'On the selected date/time, some study staff members have a personal block:')}
            </p>
            <div className={`space-y-1.5 border rounded-sm p-3 ${coverageWarning?.coverage === 'none' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
              {(coverageWarning?.blocked || []).map(b => (
                <div key={b.block_id} className="flex items-start gap-2 text-xs" data-testid={`cov-blocked-${b.user_id}`}>
                  <span className={`inline-block w-1.5 h-1.5 rounded-full mt-1.5 ${coverageWarning?.coverage === 'none' ? 'bg-red-500' : 'bg-amber-500'}`} />
                  <div className="flex-1">
                    <span className="font-semibold text-stone-800">{b.name}</span>
                    <span className="text-stone-500">
                      {' — '}
                      {b.all_day
                        ? (lang === 'es' ? 'todo el día' : 'all day')
                        : `${b.start_time}–${b.end_time}`}
                    </span>
                    <div className="text-stone-600 italic">{b.reason}</div>
                  </div>
                </div>
              ))}
            </div>
            {coverageWarning?.coverage === 'partial' && (
              <>
                <p className="text-stone-700">
                  {lang === 'es'
                    ? `Atenderán las citas: ${(coverageWarning?.available || []).map(a => a.name).join(', ') || '—'}.`
                    : `Available: ${(coverageWarning?.available || []).map(a => a.name).join(', ') || '—'}.`}
                </p>
                <label className="flex items-center gap-2 text-xs text-stone-700 bg-stone-50 border border-stone-200 rounded-sm p-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={coverageAck}
                    onChange={e => setCoverageAck(e.target.checked)}
                    data-testid="coverage-ack-checkbox"
                  />
                  <span>
                    {lang === 'es'
                      ? `He leído el aviso y confirmo ${editing ? 'el cambio' : 'la creación'} de la cita.`
                      : 'I have read this notice and confirm the appointment.'}
                  </span>
                </label>
              </>
            )}
            {coverageWarning?.coverage === 'none' && (
              <p className="text-xs text-stone-600 bg-stone-50 border border-stone-200 rounded-sm p-2">
                {lang === 'es'
                  ? 'Elige otra fecha/hora, o retira el bloqueo del profesional desde Configuración > Usuarios.'
                  : 'Choose another date/time, or remove the block from Settings > Users.'}
              </p>
            )}
          </div>
          <DialogFooter>
            {coverageWarning?.coverage === 'none' ? (
              <Button
                onClick={() => { setCoverageWarning(null); setCoverageAck(false); }}
                data-testid="coverage-close-btn"
              >
                {lang === 'es' ? 'Entendido' : 'Got it'}
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => { setCoverageWarning(null); setCoverageAck(false); }} data-testid="coverage-cancel-btn">
                  {lang === 'es' ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button
                  disabled={!coverageAck || saving}
                  onClick={() => { setCoverageWarning(null); setCoverageAck(false); doSave({ skipCoverage: true }); }}
                  data-testid="coverage-confirm-btn"
                >
                  {editing ? (lang === 'es' ? 'Continuar y guardar cambio' : 'Continue and save') : (lang === 'es' ? 'Continuar y crear cita' : 'Continue and create')}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showOutOfSchedule} onOpenChange={setShowOutOfSchedule}>
        <DialogContent className="sm:max-w-md" data-testid="out-of-schedule-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-700">
              {lang === 'es' ? 'Fuera de programacion' : 'Out of schedule'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-600">
            {lang === 'es'
              ? `La fecha y hora seleccionadas (${form.date} ${form.time}) no estan dentro de la programacion del estudio.`
              : `The selected date/time (${form.date} ${form.time}) are not within the study schedule.`}
          </p>
          {(() => {
            // List the schedule slots for this study in a +/- 3 day window
            // around the chosen date so the user can immediately see when the
            // study IS available.
            if (!form.study_id || !form.date) return null;
            const base = new Date(form.date + 'T00:00:00');
            const days = [];
            for (let off = -3; off <= 3; off += 1) {
              const d = new Date(base);
              d.setDate(d.getDate() + off);
              days.push(d.toISOString().split('T')[0]);
            }
            const slotsByDay = days.map(d => ({
              date: d,
              slots: scheduleSlots
                .filter(s => s.study_id === form.study_id && s.date === d)
                .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '')),
            }));
            const anySlot = slotsByDay.some(g => g.slots.length > 0);
            if (!anySlot) {
              return (
                <p className="text-xs text-stone-500 bg-stone-50 border border-stone-200 rounded-sm px-3 py-2" data-testid="out-of-schedule-empty">
                  {lang === 'es'
                    ? 'Este estudio no tiene franjas programadas en los proximos +/- 3 dias. Define la programacion en la Agenda o crea la cita igualmente.'
                    : 'This study has no schedule slots in the next +/- 3 days. Define the schedule in Agenda or create the appointment anyway.'}
                </p>
              );
            }
            return (
              <div className="space-y-1 bg-stone-50 border border-stone-200 rounded-sm p-2 max-h-56 overflow-y-auto" data-testid="out-of-schedule-slots">
                <p className="text-[11px] uppercase tracking-wide text-stone-500 font-semibold pb-1">
                  {lang === 'es' ? 'Franjas programadas (+/- 3 dias)' : 'Scheduled slots (+/- 3 days)'}
                </p>
                {slotsByDay.map(g => (
                  <div key={g.date} className="flex items-start gap-2 text-xs">
                    <span className={`font-mono ${g.date === form.date ? 'text-amber-700 font-semibold' : 'text-stone-500'} w-24 shrink-0`}>{g.date}</span>
                    {g.slots.length === 0 ? (
                      <span className="text-stone-400">{lang === 'es' ? 'sin franjas' : 'no slots'}</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {g.slots.map(s => (
                          <span key={s.slot_id || `${s.start_time}-${s.end_time}`}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-white border border-stone-200 text-stone-700 font-mono"
                            data-testid={`out-of-schedule-slot-${g.date}-${s.start_time}`}>
                            {s.start_time}-{s.end_time}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
          <DialogFooter className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => { setShowOutOfSchedule(false); setPendingPrint(false); }}>{lb.cancel}</Button>
            <Button data-testid="out-of-schedule-create-anyway" onClick={async () => {
              // URS-F341: si el flujo origen era "Guardar e imprimir",
              // abrimos el popup AHORA (sincrono con el click del confirm
              // para evitar el bloqueador de popups) y lo pasamos a doSave.
              const shouldPrint = pendingPrint;
              setPendingPrint(false);
              setShowOutOfSchedule(false);
              if (shouldPrint) {
                const w = window.open('', '_blank');
                if (!w) {
                  toast.error(lang === 'es' ? 'Habilita las ventanas emergentes para imprimir.' : 'Allow popups to print.');
                  await doSave();
                  return;
                }
                await doSave({ printAfter: true, printWin: w });
              } else {
                await doSave();
              }
            }} className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm">
              {editing ? (lang === 'es' ? 'Guardar igualmente' : 'Save anyway') : (lang === 'es' ? 'Crear igualmente' : 'Create anyway')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate-visit conflict (HTTP 409 from POST /api/appointments) */}
      <Dialog open={!!duplicateConflict} onOpenChange={(v) => { if (!v) setDuplicateConflict(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="duplicate-visit-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700">
              {lang === 'es' ? 'Cita duplicada' : 'Duplicate appointment'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-700">
            {lang === 'es'
              ? `Este voluntario ya tiene una cita ${duplicateConflict?.visit_code || ''} en este estudio.`
              : `This volunteer already has a ${duplicateConflict?.visit_code || ''} appointment in this study.`}
          </p>
          {duplicateConflict && (
            <div className="text-sm bg-stone-50 border border-stone-200 rounded-sm px-3 py-2 space-y-0.5" data-testid="duplicate-visit-existing">
              <div><span className="text-stone-500">{lang === 'es' ? 'Fecha' : 'Date'}:</span> <span className="font-mono">{duplicateConflict.date}</span></div>
              <div><span className="text-stone-500">{lang === 'es' ? 'Hora' : 'Time'}:</span> <span className="font-mono">{duplicateConflict.time || '-'}</span></div>
              <div><span className="text-stone-500">{lang === 'es' ? 'Estado' : 'Status'}:</span> <span className="font-mono">{duplicateConflict.status}</span></div>
            </div>
          )}
          <p className="text-xs text-stone-500">
            {lang === 'es'
              ? 'No es posible crear varias citas activas de la misma visita. Modifica la existente o anulala antes de crear una nueva.'
              : 'Multiple active appointments for the same visit are not allowed. Edit the existing one or cancel it before creating a new one.'}
          </p>
          <DialogFooter className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setDuplicateConflict(null)} data-testid="duplicate-visit-cancel">{lb.cancel}</Button>
            <Button data-testid="duplicate-visit-cancel-existing" disabled={cancellingDup}
              onClick={handleCancelDuplicate}
              className="bg-red-600 hover:bg-red-700 text-white rounded-sm">
              {cancellingDup ? '...' : (lang === 'es' ? 'Anular existente y crear' : 'Cancel existing & create')}
            </Button>
            <Button data-testid="duplicate-visit-edit-existing" onClick={handleEditDuplicate}
              className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm">
              {lang === 'es' ? 'Editar la existente' : 'Edit existing'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <VolunteerModal volunteerId={viewVolunteerId} open={!!viewVolunteerId} onClose={() => setViewVolunteerId(null)} />

      <ConfirmDeleteDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title={lang === 'es' ? 'Eliminar cita' : 'Delete appointment'}
        description={lang === 'es' ? '¿Estás seguro de que deseas eliminar esta cita? Esta acción no se puede deshacer.' : 'Are you sure you want to delete this appointment? This action cannot be undone.'}
        onConfirm={handleDelete}
        loading={deletingAppt}
      />
    </>
  );
}
